import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Dialog,
  EmptyState,
  Field,
  Grid,
  Inline,
  Panel,
  SelectField,
  Spinner,
  Stack,
  StatusBadge,
  Switch,
  useToast
} from "@leylion/design-system";
import {
  createBranch,
  getBranches,
  getBusinessHours,
  updateBranch,
  updateBusinessHours
} from "../api/client";
import type {
  Branch,
  BusinessHour,
  LocationType
} from "../api/settings-types";
import {
  formatDate,
  minutesToTime,
  timeToMinutes
} from "./settings-utils";

type Language = "ar" | "en";

const locationTypes: LocationType[] = [
  "Store",
  "Restaurant",
  "Warehouse",
  "CentralKitchen",
  "Office"
];

const emptyBranch = {
  name: "",
  code: "",
  isActive: true,
  locationType: "Store" as LocationType,
  countryCode: "AE",
  region: "",
  city: "",
  addressLine1: "",
  addressLine2: "",
  postalCode: "",
  phone: "",
  email: "",
  timeZoneOverride: "Asia/Dubai",
  currencyOverride: "AED",
  taxRegistrationNumberOverride: "",
  businessDayCutoffMinute: 240,
  latitude: null,
  longitude: null
};

export function BranchesPanel({
  language,
  onChanged
}: {
  language: Language;
  onChanged?: () => void;
}) {
  const toast = useToast();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [draft, setDraft] = useState<Branch | null>(null);
  const [original, setOriginal] = useState<Branch | null>(null);
  const [hours, setHours] = useState<BusinessHour[]>([]);
  const [originalHours, setOriginalHours] = useState<BusinessHour[]>([]);
  const [newOpen, setNewOpen] = useState(false);
  const [newBranch, setNewBranch] = useState(emptyBranch);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const ar = language === "ar";

  const dayLabels = ar
    ? ["الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"]
    : ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  const loadBranches = async (preferredId?: string) => {
    setBusy(true);
    try {
      const result = await getBranches();
      setBranches(result);
      const nextId = preferredId ?? selectedId ?? result[0]?.id ?? "";
      setSelectedId(nextId || result[0]?.id || "");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to load branches.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void loadBranches();
  }, []);

  useEffect(() => {
    const selected = branches.find((branch) => branch.id === selectedId) ?? null;
    setDraft(selected ? structuredClone(selected) : null);
    setOriginal(selected ? structuredClone(selected) : null);

    if (!selected) {
      setHours([]);
      setOriginalHours([]);
      return;
    }

    getBusinessHours(selected.id)
      .then((result) => {
        setHours(result);
        setOriginalHours(structuredClone(result));
      })
      .catch((caught: unknown) =>
        setError(caught instanceof Error ? caught.message : "Unable to load hours.")
      );
  }, [selectedId, branches]);

  const branchChanged = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(original),
    [draft, original]
  );
  const hoursChanged = useMemo(
    () => JSON.stringify(hours) !== JSON.stringify(originalHours),
    [hours, originalHours]
  );

  const updateDraft = <K extends keyof Branch>(key: K, value: Branch[K]) => {
    setDraft((current) => (current ? { ...current, [key]: value } : current));
  };

  const saveBranch = async () => {
    if (!draft) return;
    setBusy(true);
    setError("");
    try {
      const saved = await updateBranch(draft);
      setDraft(saved);
      setOriginal(structuredClone(saved));
      setBranches((current) =>
        current.map((branch) => (branch.id === saved.id ? saved : branch))
      );
      toast.notify({
        tone: "success",
        title: ar ? "تم حفظ الفرع" : "Branch saved"
      });
      onChanged?.();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to save branch.");
    } finally {
      setBusy(false);
    }
  };

  const saveHours = async () => {
    if (!draft) return;
    setBusy(true);
    setError("");
    try {
      const saved = await updateBusinessHours(draft.id, hours);
      setHours(saved);
      setOriginalHours(structuredClone(saved));
      toast.notify({
        tone: "success",
        title: ar ? "تم حفظ ساعات العمل" : "Business hours saved"
      });
      onChanged?.();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to save hours.");
    } finally {
      setBusy(false);
    }
  };

  const addBranch = async () => {
    setBusy(true);
    setError("");
    try {
      const created = await createBranch({
        ...newBranch,
        region: newBranch.region || null,
        city: newBranch.city || null,
        addressLine1: newBranch.addressLine1 || null,
        addressLine2: newBranch.addressLine2 || null,
        postalCode: newBranch.postalCode || null,
        phone: newBranch.phone || null,
        email: newBranch.email || null,
        timeZoneOverride: newBranch.timeZoneOverride || null,
        currencyOverride: newBranch.currencyOverride || null,
        taxRegistrationNumberOverride:
          newBranch.taxRegistrationNumberOverride || null
      });
      setNewOpen(false);
      setNewBranch(emptyBranch);
      await loadBranches(created.id);
      toast.notify({
        tone: "success",
        title: ar ? "تم إنشاء الفرع" : "Branch created"
      });
      onChanged?.();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to create branch.");
      setBusy(false);
    }
  };

  if (busy && branches.length === 0) {
    return (
      <Card>
        <Inline>
          <Spinner label={ar ? "جارٍ التحميل" : "Loading"} />
          <span>{ar ? "جارٍ تحميل الفروع…" : "Loading branches…"}</span>
        </Inline>
      </Card>
    );
  }

  return (
    <Stack>
      <Panel
        title={ar ? "الفروع والمواقع" : "Branches & Locations"}
        description={
          ar
            ? "إدارة الفروع والمستودعات والمطابخ وساعات العمل ضمن المؤسسة."
            : "Manage branches, warehouses, kitchens and business hours within the organization."
        }
        actions={
          <Button type="button" icon="plus" onClick={() => setNewOpen(true)}>
            {ar ? "إضافة فرع" : "Add branch"}
          </Button>
        }
      >
        <Stack>
          {error ? (
            <Alert tone="danger" title={ar ? "تعذر تنفيذ الإجراء" : "Action failed"}>
              {error}
            </Alert>
          ) : null}

          <div className="branches-layout">
            <aside className="branches-list">
              {branches.map((branch) => (
                <button
                  key={branch.id}
                  type="button"
                  className={branch.id === selectedId ? "is-active" : ""}
                  onClick={() => setSelectedId(branch.id)}
                >
                  <span>
                    <strong>{branch.name}</strong>
                    <small>{branch.code} · {branch.locationType}</small>
                  </span>
                  <StatusBadge tone={branch.isActive ? "success" : "neutral"}>
                    {branch.isActive ? (ar ? "نشط" : "Active") : (ar ? "متوقف" : "Inactive")}
                  </StatusBadge>
                </button>
              ))}
            </aside>

            <div className="branches-editor">
              {!draft ? (
                <EmptyState
                  icon="settings"
                  title={ar ? "اختر فرعًا" : "Select a branch"}
                  description={
                    ar
                      ? "اختر موقعًا من القائمة لتعديل بياناته."
                      : "Choose a location from the list to edit its details."
                  }
                />
              ) : (
                <Stack>
                  <Card
                    title={ar ? "بيانات الفرع" : "Branch details"}
                    actions={
                      <Button
                        type="button"
                        disabled={busy || !branchChanged}
                        onClick={() => void saveBranch()}
                      >
                        {ar ? "حفظ الفرع" : "Save branch"}
                      </Button>
                    }
                  >
                    <Grid columns={2}>
                      <Field
                        label={ar ? "اسم الفرع" : "Branch name"}
                        value={draft.name}
                        onChange={(event) => updateDraft("name", event.target.value)}
                      />
                      <Field
                        label={ar ? "رمز الفرع" : "Branch code"}
                        value={draft.code}
                        onChange={(event) =>
                          updateDraft("code", event.target.value.toUpperCase())
                        }
                      />
                      <SelectField
                        label={ar ? "نوع الموقع" : "Location type"}
                        value={draft.locationType}
                        onChange={(event) =>
                          updateDraft("locationType", event.target.value as LocationType)
                        }
                      >
                        {locationTypes.map((type) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </SelectField>
                      <Switch
                        label={ar ? "الفرع نشط" : "Branch is active"}
                        checked={draft.isActive}
                        onChange={(event) => updateDraft("isActive", event.target.checked)}
                      />
                      <Field
                        label={ar ? "الدولة" : "Country"}
                        value={draft.countryCode}
                        maxLength={2}
                        onChange={(event) =>
                          updateDraft("countryCode", event.target.value.toUpperCase())
                        }
                      />
                      <Field
                        label={ar ? "الإمارة / المنطقة" : "Region"}
                        value={draft.region ?? ""}
                        onChange={(event) => updateDraft("region", event.target.value || null)}
                      />
                      <Field
                        label={ar ? "المدينة" : "City"}
                        value={draft.city ?? ""}
                        onChange={(event) => updateDraft("city", event.target.value || null)}
                      />
                      <Field
                        label={ar ? "العنوان الأول" : "Address line 1"}
                        value={draft.addressLine1 ?? ""}
                        onChange={(event) =>
                          updateDraft("addressLine1", event.target.value || null)
                        }
                      />
                      <Field
                        label={ar ? "العنوان الثاني" : "Address line 2"}
                        value={draft.addressLine2 ?? ""}
                        onChange={(event) =>
                          updateDraft("addressLine2", event.target.value || null)
                        }
                      />
                      <Field
                        label={ar ? "الهاتف" : "Phone"}
                        value={draft.phone ?? ""}
                        onChange={(event) => updateDraft("phone", event.target.value || null)}
                      />
                      <Field
                        label={ar ? "البريد" : "Email"}
                        type="email"
                        value={draft.email ?? ""}
                        onChange={(event) => updateDraft("email", event.target.value || null)}
                      />
                      <Field
                        label={ar ? "المنطقة الزمنية المخصصة" : "Time zone override"}
                        value={draft.timeZoneOverride ?? ""}
                        onChange={(event) =>
                          updateDraft("timeZoneOverride", event.target.value || null)
                        }
                      />
                      <Field
                        label={ar ? "العملة المخصصة" : "Currency override"}
                        value={draft.currencyOverride ?? ""}
                        maxLength={3}
                        onChange={(event) =>
                          updateDraft(
                            "currencyOverride",
                            event.target.value.toUpperCase() || null
                          )
                        }
                      />
                      <Field
                        label={ar ? "دقيقة إغلاق يوم العمل" : "Business day cutoff minute"}
                        type="number"
                        min={0}
                        max={1439}
                        value={draft.businessDayCutoffMinute}
                        onChange={(event) =>
                          updateDraft(
                            "businessDayCutoffMinute",
                            Number(event.target.value)
                          )
                        }
                      />
                      <Field
                        label={ar ? "خط العرض" : "Latitude"}
                        type="number"
                        step="0.000001"
                        value={draft.latitude ?? ""}
                        onChange={(event) =>
                          updateDraft(
                            "latitude",
                            event.target.value ? Number(event.target.value) : null
                          )
                        }
                      />
                      <Field
                        label={ar ? "خط الطول" : "Longitude"}
                        type="number"
                        step="0.000001"
                        value={draft.longitude ?? ""}
                        onChange={(event) =>
                          updateDraft(
                            "longitude",
                            event.target.value ? Number(event.target.value) : null
                          )
                        }
                      />
                    </Grid>
                    <p className="settings-meta">
                      {ar ? "آخر تحديث" : "Last updated"}:{" "}
                      {formatDate(draft.updatedAt, language)} ·{" "}
                      {ar ? "الإصدار" : "Version"} {draft.version}
                    </p>
                  </Card>

                  <Card
                    title={ar ? "ساعات العمل" : "Business hours"}
                    actions={
                      <Button
                        type="button"
                        disabled={busy || !hoursChanged}
                        onClick={() => void saveHours()}
                      >
                        {ar ? "حفظ الساعات" : "Save hours"}
                      </Button>
                    }
                  >
                    <div className="business-hours">
                      {hours.map((hour, index) => (
                        <div className="business-hours__row" key={hour.dayOfWeek}>
                          <strong>{dayLabels[hour.dayOfWeek]}</strong>
                          <Switch
                            label={ar ? "مغلق" : "Closed"}
                            checked={hour.isClosed}
                            onChange={(event) =>
                              setHours((current) =>
                                current.map((item, itemIndex) =>
                                  itemIndex === index
                                    ? { ...item, isClosed: event.target.checked }
                                    : item
                                )
                              )
                            }
                          />
                          <Field
                            label={ar ? "من" : "From"}
                            type="time"
                            value={minutesToTime(hour.openMinute)}
                            disabled={hour.isClosed}
                            onChange={(event) =>
                              setHours((current) =>
                                current.map((item, itemIndex) =>
                                  itemIndex === index
                                    ? { ...item, openMinute: timeToMinutes(event.target.value) }
                                    : item
                                )
                              )
                            }
                          />
                          <Field
                            label={ar ? "إلى" : "To"}
                            type="time"
                            value={minutesToTime(hour.closeMinute)}
                            disabled={hour.isClosed}
                            onChange={(event) =>
                              setHours((current) =>
                                current.map((item, itemIndex) =>
                                  itemIndex === index
                                    ? { ...item, closeMinute: timeToMinutes(event.target.value) }
                                    : item
                                )
                              )
                            }
                          />
                          <Switch
                            label={ar ? "يمتد لليوم التالي" : "Spans next day"}
                            checked={hour.spansNextDay}
                            disabled={hour.isClosed}
                            onChange={(event) =>
                              setHours((current) =>
                                current.map((item, itemIndex) =>
                                  itemIndex === index
                                    ? { ...item, spansNextDay: event.target.checked }
                                    : item
                                )
                              )
                            }
                          />
                        </div>
                      ))}
                    </div>
                  </Card>
                </Stack>
              )}
            </div>
          </div>
        </Stack>
      </Panel>

      <Dialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        title={ar ? "إضافة فرع جديد" : "Add new branch"}
        description={
          ar
            ? "سيتم إنشاء الموقع مع ساعات عمل افتراضية وإتاحته لنظام الإعدادات."
            : "The location will be created with default business hours and made available to settings scopes."
        }
        footer={
          <Inline>
            <Button type="button" variant="secondary" onClick={() => setNewOpen(false)}>
              {ar ? "إلغاء" : "Cancel"}
            </Button>
            <Button
              type="button"
              disabled={busy || !newBranch.name || !newBranch.code}
              onClick={() => void addBranch()}
            >
              {ar ? "إنشاء الفرع" : "Create branch"}
            </Button>
          </Inline>
        }
      >
        <Grid columns={2}>
          <Field
            label={ar ? "اسم الفرع" : "Branch name"}
            value={newBranch.name}
            onChange={(event) =>
              setNewBranch((current) => ({ ...current, name: event.target.value }))
            }
          />
          <Field
            label={ar ? "رمز الفرع" : "Branch code"}
            value={newBranch.code}
            onChange={(event) =>
              setNewBranch((current) => ({
                ...current,
                code: event.target.value.toUpperCase()
              }))
            }
          />
          <SelectField
            label={ar ? "نوع الموقع" : "Location type"}
            value={newBranch.locationType}
            onChange={(event) =>
              setNewBranch((current) => ({
                ...current,
                locationType: event.target.value as LocationType
              }))
            }
          >
            {locationTypes.map((type) => <option key={type}>{type}</option>)}
          </SelectField>
          <Field
            label={ar ? "المدينة" : "City"}
            value={newBranch.city}
            onChange={(event) =>
              setNewBranch((current) => ({ ...current, city: event.target.value }))
            }
          />
          <Field
            label={ar ? "العنوان" : "Address"}
            value={newBranch.addressLine1}
            onChange={(event) =>
              setNewBranch((current) => ({
                ...current,
                addressLine1: event.target.value
              }))
            }
          />
          <Field
            label={ar ? "الهاتف" : "Phone"}
            value={newBranch.phone}
            onChange={(event) =>
              setNewBranch((current) => ({ ...current, phone: event.target.value }))
            }
          />
        </Grid>
      </Dialog>
    </Stack>
  );
}
